/**
 * Subagents route — UI for managing .claude/agents/*.md definitions.
 *
 * Backed by /api/subagents (phase 2). Claude Code reads those files
 * directly to decide which subagents to spawn when the Agent/Task tool
 * is available — Black Wall only manages the definitions.
 *
 * Layout:
 *   Top: header with "New subagent" action
 *   Main: grid of cards (one per .md file) with color stripe + summary
 *   Drawer: click a card → slide-in editor with frontmatter form + body
 *           editor. Validate on change, save writes through PUT.
 */

import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Bot,
  Pencil,
  Trash2,
  Save,
  X,
  Copy,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RotateCcw,
  FileText,
  Sparkles,
} from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";
import { CodeMirrorEditor } from "../components/editor/CodeMirrorEditor";

// ============================================================================
// Types (mirror the backend shapes from services/subagents.ts)
// ============================================================================

interface SubagentFrontmatter {
  name: string;
  description: string;
  model?: string;
  tools?: string[];
  disallowedTools?: string[];
  permissionMode?: string;
  maxTurns?: number;
  effort?: string;
  color?: string;
  skills?: string[];
  initialPrompt?: string;
  background?: boolean;
  [extraField: string]: unknown;
}

interface SubagentSummary {
  slug: string;
  name: string;
  description: string;
  model: string | null;
  color: string | null;
  toolsCount: number;
  hasCustomPermissionMode: boolean;
  mtimeMs: number;
  sizeBytes: number;
  scope: "global";
}

interface SubagentDetail {
  slug: string;
  scope: "global";
  frontmatter: SubagentFrontmatter;
  body: string;
  mtimeMs: number;
  sizeBytes: number;
  path: string;
}

interface ListResponse {
  subagents: SubagentSummary[];
  count: number;
}

interface DetailResponse {
  subagent: SubagentDetail;
}

interface ValidationResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
}

// Known valid values for dropdowns. Kept in sync with
// services/subagents.ts VALID_PERMISSION_MODES/VALID_EFFORT_LEVELS.
const PERMISSION_MODES = [
  "",
  "default",
  "auto",
  "acceptEdits",
  "plan",
  "bypassPermissions",
  "dontAsk",
] as const;

const EFFORT_LEVELS = ["", "low", "medium", "high"] as const;

const MODEL_ALIASES = [
  "",
  "inherit",
  "haiku",
  "sonnet",
  "opus",
  "claude-haiku-4-5",
  "claude-sonnet-4-5",
  "claude-opus-4-6",
] as const;

// Default body for a brand-new subagent. Provides a usable starting
// point instead of an empty editor.
const DEFAULT_BODY = `You are a specialized subagent.

## Role
Describe the agent's expertise here.

## Rules
- Rule 1
- Rule 2

## Output
Describe what the agent should return when it finishes.
`;

// ============================================================================
// Card component — compact list item
// ============================================================================

function SubagentCard({
  subagent,
  onEdit,
  onDelete,
  onDuplicate,
}: {
  subagent: SubagentSummary;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const accentColor = subagent.color ?? "var(--bw-accent)";

  return (
    <CorpoCard
      className="relative cursor-pointer group"
      accent
      style={
        {
          borderLeftColor: accentColor,
          borderLeftWidth: "3px",
        } as React.CSSProperties
      }
      onClick={onEdit}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-sm flex-shrink-0"
          style={{
            backgroundColor: `${accentColor}20`,
            border: `1px solid ${accentColor}`,
          }}
        >
          <Bot
            className="w-6 h-6"
            style={{ color: accentColor } as React.CSSProperties}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="font-bold text-lg uppercase tracking-wide truncate"
            style={{ color: accentColor }}
            title={subagent.name}
          >
            {subagent.name}
          </h3>
          <p className="text-xs text-[var(--bw-muted)] uppercase">
            {subagent.scope} · {(subagent.sizeBytes / 1024).toFixed(1)}kB
          </p>
        </div>
      </div>

      <p className="text-sm text-[var(--bw-fg)] mb-4 line-clamp-2 min-h-[2.5em]">
        {subagent.description}
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {subagent.model && (
          <HexBadge variant="primary">{subagent.model}</HexBadge>
        )}
        {subagent.toolsCount > 0 && (
          <HexBadge variant="muted">
            {subagent.toolsCount} tool{subagent.toolsCount === 1 ? "" : "s"}
          </HexBadge>
        )}
        {subagent.hasCustomPermissionMode && (
          <HexBadge variant="warn">custom perms</HexBadge>
        )}
      </div>

      <div
        className="flex gap-2 opacity-60 group-hover:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <GlyphButton
          variant="secondary"
          size="sm"
          onClick={onEdit}
          icon={<Pencil size={14} />}
        >
          EDIT
        </GlyphButton>
        <GlyphButton
          variant="ghost"
          size="sm"
          onClick={onDuplicate}
          icon={<Copy size={14} />}
          title="Duplicate as a new subagent"
        >
          DUP
        </GlyphButton>
        <GlyphButton
          variant="ghost"
          size="sm"
          onClick={onDelete}
          icon={<Trash2 size={14} />}
          className="hover:text-[var(--bw-danger)]"
        >
          DEL
        </GlyphButton>
      </div>
    </CorpoCard>
  );
}

// ============================================================================
// Editor drawer — full CRUD form with CodeMirror for the body
// ============================================================================

interface EditorState {
  mode: "create" | "edit";
  // For edit mode: original slug (locked, immutable). For create: empty.
  originalSlug: string;
  // Working copy being edited. slug is only editable in create mode.
  slug: string;
  frontmatter: SubagentFrontmatter;
  body: string;
}

function SubagentEditor({
  state,
  onClose,
  onSaved,
}: {
  state: EditorState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<EditorState>(state);
  const [toolsText, setToolsText] = useState(
    (draft.frontmatter.tools ?? []).join(", "),
  );
  const [skillsText, setSkillsText] = useState(
    (draft.frontmatter.skills ?? []).join(", "),
  );
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Derived: effective frontmatter combining form state + parsed csv fields
  const effectiveFrontmatter = useMemo<SubagentFrontmatter>(() => {
    const tools = toolsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const skills = skillsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const fm: SubagentFrontmatter = {
      ...draft.frontmatter,
      name: draft.mode === "create" ? draft.slug : draft.originalSlug,
    };
    if (tools.length > 0) {
      fm.tools = tools;
    } else {
      delete fm.tools;
    }
    if (skills.length > 0) {
      fm.skills = skills;
    } else {
      delete fm.skills;
    }
    // Clean empty-string optional fields so they don't land in the YAML
    for (const key of ["model", "permissionMode", "effort", "color", "initialPrompt"] as const) {
      if (fm[key] === "" || fm[key] === undefined) {
        delete fm[key];
      }
    }
    if (fm.maxTurns === undefined || Number.isNaN(fm.maxTurns)) {
      delete fm.maxTurns;
    }
    return fm;
  }, [draft, toolsText, skillsText]);

  // Live validation via POST /api/subagents/validate (debounced)
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch("/api/subagents/validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            slug: draft.mode === "create" ? draft.slug : draft.originalSlug,
            frontmatter: effectiveFrontmatter,
          }),
          signal: controller.signal,
        });
        const data = (await res.json()) as ValidationResult;
        setValidation(data);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.error("validate failed", err);
        }
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [effectiveFrontmatter, draft.slug, draft.originalSlug, draft.mode]);

  // Save mutation — POST for create, PUT for update
  const saveMutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      if (draft.mode === "create") {
        const res = await fetch("/api/subagents", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            slug: draft.slug,
            frontmatter: effectiveFrontmatter,
            body: draft.body,
          }),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error ?? `HTTP ${res.status}`);
        }
        return res.json();
      } else {
        const res = await fetch(`/api/subagents/${draft.originalSlug}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            frontmatter: effectiveFrontmatter,
            body: draft.body,
          }),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error ?? `HTTP ${res.status}`);
        }
        return res.json();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subagents"] });
      queryClient.invalidateQueries({ queryKey: ["subagent", draft.originalSlug] });
      onSaved();
    },
    onError: (err: Error) => {
      setSaveError(err.message);
    },
  });

  const canSave = validation?.ok !== false && draft.body.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Drawer */}
      <div
        className="relative ml-auto w-full max-w-3xl h-full bg-[var(--bw-bg)] border-l border-[var(--bw-border)] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--bw-border)] flex-shrink-0">
          <div className="flex items-center gap-3">
            <Bot className="w-5 h-5 text-[var(--bw-accent)]" />
            <h2 className="text-lg font-bold uppercase tracking-wide">
              {draft.mode === "create" ? "New Subagent" : `Edit: ${draft.originalSlug}`}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--bw-muted)] hover:text-[var(--bw-fg)]"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Slug */}
          <div>
            <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
              Slug {draft.mode === "edit" && "(locked)"}
            </label>
            <input
              type="text"
              value={draft.slug}
              disabled={draft.mode === "edit"}
              placeholder="sql-reviewer"
              onChange={(e) =>
                setDraft({ ...draft, slug: e.target.value.toLowerCase() })
              }
              className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 font-mono text-sm disabled:opacity-50"
            />
            <p className="text-xs text-[var(--bw-muted)] mt-1">
              Lowercase, alphanumerics + hyphens, 1-63 chars. Used as the
              filename and the Claude Code agent name.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
              Description <span className="text-[var(--bw-danger)]">*</span>
            </label>
            <textarea
              value={draft.frontmatter.description}
              placeholder="Reviews SQL queries against SistemasBAZ for correctness and performance…"
              onChange={(e) =>
                setDraft({
                  ...draft,
                  frontmatter: { ...draft.frontmatter, description: e.target.value },
                })
              }
              rows={3}
              className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm"
            />
            <p className="text-xs text-[var(--bw-muted)] mt-1">
              Claude Code reads this to decide when to delegate. Be specific
              about when this subagent should run.
            </p>
          </div>

          {/* Model / Color / Effort in a row */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
                Model
              </label>
              <select
                value={(draft.frontmatter.model as string) ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    frontmatter: {
                      ...draft.frontmatter,
                      model: e.target.value || undefined,
                    },
                  })
                }
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm"
              >
                {MODEL_ALIASES.map((m) => (
                  <option key={m} value={m}>
                    {m || "(inherit from parent)"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
                Effort
              </label>
              <select
                value={(draft.frontmatter.effort as string) ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    frontmatter: {
                      ...draft.frontmatter,
                      effort: e.target.value || undefined,
                    },
                  })
                }
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm"
              >
                {EFFORT_LEVELS.map((e) => (
                  <option key={e} value={e}>
                    {e || "(default)"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
                Color
              </label>
              <div className="flex gap-1">
                <input
                  type="color"
                  value={(draft.frontmatter.color as string) ?? "#00f0ff"}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      frontmatter: {
                        ...draft.frontmatter,
                        color: e.target.value,
                      },
                    })
                  }
                  className="w-10 h-9 bg-[var(--bw-surface)] border border-[var(--bw-border)] cursor-pointer"
                />
                <input
                  type="text"
                  value={(draft.frontmatter.color as string) ?? ""}
                  placeholder="#00f0ff"
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      frontmatter: {
                        ...draft.frontmatter,
                        color: e.target.value || undefined,
                      },
                    })
                  }
                  className="flex-1 bg-[var(--bw-surface)] border border-[var(--bw-border)] px-2 py-2 font-mono text-xs"
                />
              </div>
            </div>
          </div>

          {/* Permission mode + max turns */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
                Permission mode
              </label>
              <select
                value={(draft.frontmatter.permissionMode as string) ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    frontmatter: {
                      ...draft.frontmatter,
                      permissionMode: e.target.value || undefined,
                    },
                  })
                }
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm"
              >
                {PERMISSION_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m || "(inherit from parent)"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
                Max turns
              </label>
              <input
                type="number"
                min={1}
                max={500}
                value={(draft.frontmatter.maxTurns as number) ?? ""}
                placeholder="unlimited"
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    frontmatter: {
                      ...draft.frontmatter,
                      maxTurns: e.target.value
                        ? Number(e.target.value)
                        : undefined,
                    },
                  })
                }
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Tools */}
          <div>
            <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
              Tools (comma-separated allowlist)
            </label>
            <input
              type="text"
              value={toolsText}
              placeholder="Read, Grep, Bash(git:*)"
              onChange={(e) => setToolsText(e.target.value)}
              className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 font-mono text-xs"
            />
            <p className="text-xs text-[var(--bw-muted)] mt-1">
              Leave empty to inherit parent tools. Common: Read, Grep,
              Glob, Edit, Bash, WebFetch, WebSearch, Agent, Task.
            </p>
          </div>

          {/* Skills */}
          <div>
            <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
              Skills (comma-separated)
            </label>
            <input
              type="text"
              value={skillsText}
              placeholder="openai-whisper-api, weather"
              onChange={(e) => setSkillsText(e.target.value)}
              className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 font-mono text-xs"
            />
          </div>

          {/* Body / system prompt */}
          <div>
            <label className="block text-xs uppercase text-[var(--bw-muted)] mb-1">
              System Prompt (markdown body) <span className="text-[var(--bw-danger)]">*</span>
            </label>
            <div className="border border-[var(--bw-border)] bg-[var(--bw-surface)]">
              <CodeMirrorEditor
                value={draft.body}
                onChange={(value) => setDraft({ ...draft, body: value })}
                language="markdown"
                height="400px"
              />
            </div>
            <p className="text-xs text-[var(--bw-muted)] mt-1">
              {(draft.body.length / 1024).toFixed(1)}kB · this is written
              below the YAML frontmatter in the .md file.
            </p>
          </div>

          {/* Validation feedback */}
          {validation && (
            <div className="space-y-2">
              {validation.errors.map((err, i) => (
                <div
                  key={`err-${i}`}
                  className="flex items-start gap-2 p-2 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)]/30 text-[var(--bw-danger)] text-sm"
                >
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              ))}
              {validation.warnings.map((warn, i) => (
                <div
                  key={`warn-${i}`}
                  className="flex items-start gap-2 p-2 bg-[var(--bw-warning)]/10 border border-[var(--bw-warning)]/30 text-[var(--bw-warning)] text-sm"
                >
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{warn}</span>
                </div>
              ))}
              {validation.ok && validation.errors.length === 0 && (
                <div className="flex items-center gap-2 p-2 bg-[var(--bw-success)]/10 border border-[var(--bw-success)]/30 text-[var(--bw-success)] text-sm">
                  <CheckCircle2 size={14} />
                  <span>Frontmatter valid. Claude Code will accept this subagent.</span>
                </div>
              )}
            </div>
          )}

          {/* Save error */}
          {saveError && (
            <div className="flex items-start gap-2 p-3 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] text-[var(--bw-danger)] text-sm">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold uppercase text-xs">Save failed</div>
                <div>{saveError}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-2 p-4 border-t border-[var(--bw-border)] flex-shrink-0 bg-[var(--bw-surface)]">
          <div className="text-xs text-[var(--bw-muted)]">
            {draft.mode === "edit" && (
              <span>
                <FileText size={12} className="inline mr-1" />
                ~/.claude/agents/{draft.originalSlug}.md
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <GlyphButton
              variant="ghost"
              size="sm"
              onClick={onClose}
              icon={<X size={14} />}
            >
              CANCEL
            </GlyphButton>
            <GlyphButton
              variant="primary"
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={!canSave || saveMutation.isPending}
              icon={
                saveMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )
              }
            >
              {draft.mode === "create" ? "CREATE" : "SAVE"}
            </GlyphButton>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Delete confirmation dialog
// ============================================================================

function DeleteConfirmation({
  slug,
  onCancel,
  onConfirmed,
}: {
  slug: string;
  onCancel: () => void;
  onConfirmed: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/subagents/${slug}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subagents"] });
      onConfirmed();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      onClick={onCancel}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-[var(--bw-bg)] border border-[var(--bw-danger)] p-6 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 grid place-items-center bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)]">
            <AlertTriangle className="w-5 h-5 text-[var(--bw-danger)]" />
          </div>
          <h3 className="text-lg font-bold uppercase">Delete subagent?</h3>
        </div>
        <p className="text-sm text-[var(--bw-fg)] mb-2">
          This will permanently delete{" "}
          <code className="text-[var(--bw-accent)]">~/.claude/agents/{slug}.md</code>.
        </p>
        <p className="text-xs text-[var(--bw-muted)] mb-4">
          Historical runs of this subagent stay in the database for
          auditability. Scheduled crons that reference it will fail at
          dispatch time with a clear error.
        </p>
        {error && (
          <div className="mb-4 p-2 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] text-[var(--bw-danger)] text-xs">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <GlyphButton variant="ghost" size="sm" onClick={onCancel}>
            CANCEL
          </GlyphButton>
          <GlyphButton
            variant="primary"
            size="sm"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            icon={
              deleteMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )
            }
            className="!bg-[var(--bw-danger)] !border-[var(--bw-danger)]"
          >
            DELETE
          </GlyphButton>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main route component
// ============================================================================

export function SubagentsRoute() {
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<ListResponse>({
    queryKey: ["subagents"],
    queryFn: async () => {
      const res = await fetch("/api/subagents");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  const openCreate = () => {
    setEditorState({
      mode: "create",
      originalSlug: "",
      slug: "",
      frontmatter: {
        name: "",
        description: "",
        model: "haiku",
        color: "#00f0ff",
      },
      body: DEFAULT_BODY,
    });
  };

  const openEdit = async (slug: string) => {
    try {
      const res = await fetch(`/api/subagents/${slug}`);
      if (!res.ok) {
        const err = await res.json();
        alert(`Failed to load subagent: ${err.error ?? res.status}`);
        return;
      }
      const { subagent } = (await res.json()) as DetailResponse;
      setEditorState({
        mode: "edit",
        originalSlug: slug,
        slug,
        frontmatter: subagent.frontmatter,
        body: subagent.body,
      });
    } catch (err) {
      alert(`Failed to load subagent: ${(err as Error).message}`);
    }
  };

  const openDuplicate = async (slug: string) => {
    try {
      const res = await fetch(`/api/subagents/${slug}`);
      if (!res.ok) return;
      const { subagent } = (await res.json()) as DetailResponse;
      setEditorState({
        mode: "create",
        originalSlug: "",
        slug: `${slug}-copy`,
        frontmatter: { ...subagent.frontmatter, name: `${slug}-copy` },
        body: subagent.body,
      });
    } catch {
      // swallow
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bot size={24} className="text-[var(--bw-accent)]" />
          <div>
            <CRTHeader text="Subagents" level={2} />
            <p className="text-xs text-[var(--bw-muted)] mt-1">
              Manage .claude/agents/*.md definitions consumed natively by Claude Code
            </p>
          </div>
        </div>
        <GlyphButton
          variant="secondary"
          onClick={openCreate}
          icon={<Plus size={14} />}
        >
          NEW SUBAGENT
        </GlyphButton>
      </div>

      <div>
        {isLoading && (
          <div className="flex items-center gap-2 text-[var(--bw-muted)]">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading subagents…</span>
          </div>
        )}

        {error && (
          <div className="p-3 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] text-[var(--bw-danger)] text-sm">
            Failed to load: {(error as Error).message}
          </div>
        )}

        {data && data.count === 0 && (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-[var(--bw-muted)] mx-auto mb-3" />
            <h3 className="text-lg font-bold uppercase tracking-wide text-[var(--bw-fg)] mb-2">
              No subagents yet
            </h3>
            <p className="text-sm text-[var(--bw-muted)] mb-4 max-w-md mx-auto">
              Subagents are specialized personalities Claude Code can
              delegate to via its native <code>Agent</code> /{" "}
              <code>Task</code> tool. Define them once here and they’re
              available across every chat session.
            </p>
            <GlyphButton
              variant="primary"
              size="sm"
              onClick={openCreate}
              icon={<Plus size={14} />}
            >
              CREATE YOUR FIRST
            </GlyphButton>
          </div>
        )}

        {data && data.count > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.subagents.map((sub) => (
              <SubagentCard
                key={sub.slug}
                subagent={sub}
                onEdit={() => openEdit(sub.slug)}
                onDuplicate={() => openDuplicate(sub.slug)}
                onDelete={() => setDeleteSlug(sub.slug)}
              />
            ))}
          </div>
        )}

        {data && data.count > 0 && (
          <div className="mt-6 pt-4 border-t border-[var(--bw-border)] text-xs text-[var(--bw-muted)]">
            <RotateCcw size={12} className="inline mr-1" />
            Subagents are consumed natively by Claude Code from{" "}
            <code className="text-[var(--bw-accent)]">~/.claude/agents/</code>.
            Run{" "}
            <code className="text-[var(--bw-accent)]">claude agents</code>{" "}
            from a terminal to see them listed alongside built-ins.
          </div>
        )}
      </div>

      {editorState && (
        <SubagentEditor
          state={editorState}
          onClose={() => setEditorState(null)}
          onSaved={() => setEditorState(null)}
        />
      )}

      {deleteSlug && (
        <DeleteConfirmation
          slug={deleteSlug}
          onCancel={() => setDeleteSlug(null)}
          onConfirmed={() => setDeleteSlug(null)}
        />
      )}
    </div>
  );
}

